import React, { useState } from "react";
import { useFormikContext } from "formik";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { usePopupState, bindTrigger, bindMenu } from "material-ui-popup-state/hooks";
import DateRangeFilterModal from "./DateRangeFilterModal";
import WarehouseFilterModal from "./WarehouseFilterModal";
import CostCenterFilterModal from "./CostCenterFilterModal";
import AssetFieldFilterModal from "./AssetFieldFilterModal";

export default function AddOptionalFiltersButtonForReceiving({
  warehouses,
  costCenters,
  reportType,
  name,
  alwaysShowDates,
  onToggleDateFilters,
  onToggleWarehouseFilters,
  onToggleCostCenterFilters,
  onToggleAssetFieldFilters,
  isLoadingFiltersData,
  onFetchFiltersData,
}) {
  const { values, setFieldValue } = useFormikContext();
  const popupState = usePopupState({ variant: "popover", popupId: "add-optional-filters-popup" });
  const [showDateRangeModal, setShowDateRangeModal] = useState(false);
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [showCostCenterModal, setShowCostCenterModal] = useState(false);
  const [showAssetFieldModal, setShowAssetFieldModal] = useState(false);

  const hasProjectSelection = values.projectId || values.projectIds === "all" || (values.projectIds && Array.isArray(values.projectIds) && values.projectIds.length > 0);
  const isMultiProjectOrSingleProject = 
    (values.projectId && values.projectId !== "all") || (values.projectIds && values.projectIds !== "all" && Array.isArray(values.projectIds) && values.projectIds.length > 0);
  
  const canShowDateFilters =
    (alwaysShowDates || name !== "Receiving Report 3") && !["assets"].includes(reportType);
  const canShowWarehouseFilters = hasProjectSelection && warehouses.length > 1;
  const canShowCostCenterFilters = isMultiProjectOrSingleProject && costCenters.length > 1;
  const canShowAssetFieldFilters = true;

  const hasAvailableFilters =
    canShowDateFilters ||
    canShowWarehouseFilters ||
    canShowCostCenterFilters ||
    canShowAssetFieldFilters;

  if (!hasAvailableFilters) {
    return null;
  }

  const handleAddDateRangeFilter = () => {
    setShowDateRangeModal(true);
    popupState.close();
  };

  const handleAddWarehouseFilter = () => {
    setShowWarehouseModal(true);
    popupState.close();
  };

  const handleAddCostCenterFilter = () => {
    setShowCostCenterModal(true);
    popupState.close();
  };

  const handleAddAssetFieldFilter = () => {
    setShowAssetFieldModal(true);
    popupState.close();
  };

  const handleCloseDateRangeModal = () => {
    setShowDateRangeModal(false);
  };

  const handleCloseWarehouseModal = () => {
    setShowWarehouseModal(false);
  };

  const handleCloseCostCenterModal = () => {
    setShowCostCenterModal(false);
  };

  const handleCloseAssetFieldModal = () => {
    setShowAssetFieldModal(false);
  };

  const handleSaveDateRange = dateRange => {
    setFieldValue("startDate", dateRange.start_date);
    setFieldValue("endDate", dateRange.end_date);
    onToggleDateFilters?.(true);
  };

  const handleSaveWarehouse = warehouseIds => {
    setFieldValue("warehouseId", warehouseIds);
    onToggleWarehouseFilters?.(true);
  };

  const handleSaveCostCenter = costCenterIds => {
    setFieldValue("costCenterId", costCenterIds);
    onToggleCostCenterFilters?.(true);
  };

  const handleSaveAssetFieldFilters = assetFieldFilters => {
    setFieldValue("assetFieldFilters", assetFieldFilters);
    onToggleAssetFieldFilters?.(true);
  };

  const handleButtonClick = (event) => {
    // Fetch project filters data when button is clicked
    if (onFetchFiltersData) {
      onFetchFiltersData();
    }
    bindTrigger(popupState).onClick(event);
  };

  return (
    <Box mt={2}>
      <Button
        variant="outlined"
        color="primary"
        endIcon={<KeyboardArrowDownIcon />}
        onClick={handleButtonClick}
        disabled={isLoadingFiltersData}
      >
        {isLoadingFiltersData ? "Loading..." : "Add Optional Filters"}
      </Button>
      <Menu
        {...bindMenu(popupState)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{ mt: 0.5 }}
      >
        {canShowDateFilters && (
          <MenuItem onClick={handleAddDateRangeFilter}>Add Date Range Filter</MenuItem>
        )}
        {canShowWarehouseFilters && (
          <MenuItem onClick={handleAddWarehouseFilter}>Add Warehouse Filter</MenuItem>
        )}
        {canShowCostCenterFilters && (
          <MenuItem onClick={handleAddCostCenterFilter}>Add Cost Center Filter</MenuItem>
        )}
        {canShowAssetFieldFilters && (
          <MenuItem onClick={handleAddAssetFieldFilter}>Add Asset Field Filters</MenuItem>
        )}
      </Menu>
      {showDateRangeModal && (
        <DateRangeFilterModal
          title="Date Range Filter"
          value={{
            start_date: values.startDate,
            end_date: values.endDate,
          }}
          onClose={handleCloseDateRangeModal}
          onSave={handleSaveDateRange}
        />
      )}
      {showWarehouseModal && (
        <WarehouseFilterModal
          value={values.warehouseId}
          warehouses={warehouses}
          onSave={handleSaveWarehouse}
          onClose={handleCloseWarehouseModal}
        />
      )}
      {showCostCenterModal && (
        <CostCenterFilterModal
          value={values.costCenterId}
          costCenters={costCenters}
          onSave={handleSaveCostCenter}
          onClose={handleCloseCostCenterModal}
        />
      )}
      {showAssetFieldModal && (
        <AssetFieldFilterModal
          value={values.assetFieldFilters || []}
          onSave={handleSaveAssetFieldFilters}
          onClose={handleCloseAssetFieldModal}
        />
      )}
    </Box>
  );
}
